import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ChatMessageDTO } from '@core/models/chat.models';

/**
 * Servicio compartido para sincronizar mensajes de chat entre múltiples
 * instancias de AnalysisChatComponent (página de pre-análisis + burbuja flotante).
 */
@Injectable({
  providedIn: 'root'
})
export class ChatMessagesService {

  private messagesMap = new Map<string, BehaviorSubject<ChatMessageDTO[]>>();
  private metadataMap = new Map<string, { assistantName: string; completenessScore: number }>();

  /**
   * Obtiene el observable de mensajes para un chatId.
   * Si no existe, crea uno nuevo vacío.
   */
  getMessages$(chatId: string): Observable<ChatMessageDTO[]> {
    return this.getOrCreateSubject(chatId).asObservable();
  }

  /**
   * Obtiene los mensajes actuales (snapshot) para un chatId.
   */
  getMessages(chatId: string): ChatMessageDTO[] {
    return this.getOrCreateSubject(chatId).getValue();
  }

  /**
   * Reemplaza todos los mensajes de un chat (usado al cargar desde el servidor).
   * Solo actualiza si hay mensajes nuevos para evitar loops.
   */
  setMessages(chatId: string, messages: ChatMessageDTO[]): void {
    const subject = this.getOrCreateSubject(chatId);
    const current = subject.getValue();
    if (messages.length !== current.length || messages.length === 0) {
      subject.next(messages);
    }
  }

  /**
   * Agrega un mensaje al chat (usado al enviar o recibir un mensaje).
   * Evita duplicados por ID.
   */
  addMessage(chatId: string, message: ChatMessageDTO): void {
    const subject = this.getOrCreateSubject(chatId);
    const current = subject.getValue();
    const exists = current.some(m => m.id === message.id);
    if (!exists) {
      subject.next([...current, message]);
    }
  }

  /**
   * Guarda metadata del chat (nombre del asistente, score de completitud).
   */
  setMetadata(chatId: string, assistantName: string, completenessScore: number): void {
    this.metadataMap.set(chatId, { assistantName, completenessScore });
  }

  getMetadata(chatId: string): { assistantName: string; completenessScore: number } | undefined {
    return this.metadataMap.get(chatId);
  }

  private getOrCreateSubject(chatId: string): BehaviorSubject<ChatMessageDTO[]> {
    if (!this.messagesMap.has(chatId)) {
      this.messagesMap.set(chatId, new BehaviorSubject<ChatMessageDTO[]>([]));
    }
    return this.messagesMap.get(chatId)!;
  }
}
